import React from "react";
import Context from "../../../../context";

import * as Yup from "yup";

import {
  IItem,
  IProvinceItem,
} from "../../../../types/common.interface";
import { useFormik } from "formik";
import { ICreateAndUpdateParent } from "../../../../types/parent.interface";
import {
  CustomButton2,
  CustomDropdownSearch,
  CustomIcon,
  CustomInput,
  CustomModal,
  CustomView, LocationDropdowns,
} from "../../../../components";

import "./index.sass";
import values from "../../../../values";
import validations from "../../../../validations";
import utils from "../../../../utils";
import Cookies from "js-cookie";
import constants from "../../../../values/constants";
import { useTranslation } from "react-i18next";
import {locationNamespace} from "../../../../types/location.namespace";

const Create: React.FC = (): React.ReactElement => {

  // # =================================================================================================================
  // # --------------------------------------------- Start Validate Values ---------------------------------------------

  const { t } = useTranslation();

  const userType = utils.stringHexConversion.reverse(Cookies.get(constants.USER.TYPE));
  const { showModal, setShowModal, createFunction, isLoading } = React.useContext(Context.ParentScreen);
  const [selectedGender, setSelectedGender] = React.useState<IItem | undefined>(undefined);
  const [dropdownErrors, setDropdownErrors] = React.useState<{[index: string]: string}>({
    province: '',
    gender  : '',
    city    : '',
  });

  const [selectedCity        , setSelectedCity        ] = React.useState<locationNamespace.city        .index | undefined>(undefined);
  const [selectedArea        , setSelectedArea        ] = React.useState<locationNamespace.area        .index | undefined>(undefined);
  const [selectedProvince    , setSelectedProvince    ] = React.useState<locationNamespace.province    .index | undefined>(undefined);
  const [selectedNeighborhood, setSelectedNeighborhood] = React.useState<locationNamespace.neighborhood.index | undefined>(undefined);

  // # ---------------------------------------------- End Validate Values ----------------------------------------------
  // # =================================================================================================================

  // * =================================================================================================================
  // * -------------------------------------------- Start Local Functions ----------------------------------------------

  const formik = useFormik<ICreateAndUpdateParent>({
    enableReinitialize: true,
    initialValues: {
      lastName   : undefined,
      firstName  : undefined,
      phoneNumber: undefined,
    },
    validationSchema: Yup.object({
      lastName   : validations.yupValidations.lastName   ,
      firstName  : validations.yupValidations.firstName  ,
      phoneNumber: validations.yupValidations.phoneNumber,
    }),
    onSubmit: (values: ICreateAndUpdateParent, { setSubmitting }) => {
      const formValues: ICreateAndUpdateParent = {
        ...values,
        gender: selectedGender,

        area        : selectedArea        ?._id,
        city        : selectedCity        ?._id,
        province    : selectedProvince    ?._id,
        neighborhood: selectedNeighborhood?._id,
      };
      if (selectedGender && selectedCity && selectedProvince) {
        Promise.resolve(createFunction!(formValues)).catch((e) =>
          setSubmitting(false)
        );
      }
    },
  });

  async function submitHandler(): Promise<void> {
    let errors = {province: '', gender: '', city: ''};
    if (!selectedCity    ) errors = { ...errors, city  : t('city_is_required'  ) };
    if (!selectedGender  ) errors = { ...errors, gender: t('gender_is_required') };
    if (!selectedProvince) errors = {
      ...errors,
      province: t('province_is_required'),
      city    : t('city_is_required'    ),
    };

    setDropdownErrors(errors);
    await formik.submitForm();
  }

  function onSelectProvince(province: locationNamespace.province.index): void {
    setSelectedNeighborhood(undefined);
    setSelectedProvince    (province );
    setSelectedCity        (undefined);
    setSelectedArea        (undefined);
    setDropdownErrors({
      ...dropdownErrors,
      province: '',
      city    : '',
    });
  }

  function onSelectCity(city: locationNamespace.city.index): void {
    setSelectedNeighborhood(undefined);
    setSelectedCity        (city     );
    setSelectedArea        (undefined);
    setDropdownErrors({
      ...dropdownErrors,
      city: '',
    });
  }

  function onSelectArea(area: locationNamespace.area.index): void {
    setSelectedNeighborhood(undefined);
    setSelectedArea        (area     );
  }

  function onSelectNieghborhood(neighborhood: locationNamespace.neighborhood.index): void {
    setSelectedNeighborhood(neighborhood);
  }

  // * --------------------------------------------- End Local Functions -----------------------------------------------
  // * =================================================================================================================

  return (
    <section id="parent-create-modal-container">
      <CustomIcon
        icon="fa-sharp fa-solid fa-circle-plus"
        size="medium"
        hoverTitle={t("createParent")}
        onClick={() => {setShowModal!("CREATE");}}
      />
      <form onSubmit={submitHandler}>
        <CustomModal onClose={() => setShowModal!("NONE")} open={showModal === "CREATE"}>
          <CustomView.Container className="create-form-content">
            <CustomView.Row className="form-row">
              <CustomView.Column medium={6} className="form-colum">
                <CustomInput
                  id="firstName"
                  name="firstName"
                  label={t("firstName")}
                  value={formik.values.firstName}
                  onValueChange={formik.handleChange}
                  formikProps={{ onBlur: formik.handleBlur }}
                  error={formik.errors.firstName && formik.touched.firstName}
                  errorText={formik.errors.firstName}
                />
              </CustomView.Column>
              <CustomView.Column medium={6} className="form-colum">
                <CustomInput
                  id="lastName"
                  name="lastName"
                  label={t("lastName")}
                  value={formik.values.lastName}
                  onValueChange={formik.handleChange}
                  formikProps={{ onBlur: formik.handleBlur }}
                  error={formik.errors.lastName && formik.touched.lastName}
                  errorText={formik.errors.lastName}
                />
              </CustomView.Column>
            </CustomView.Row>
            {(userType === "ADMIN" || userType === "COMPANY") && (
              <>
                <CustomView.Row className="form-row">
                  <CustomView.Column medium={6} className="form-colum dropdown">
                    <LocationDropdowns.Province
                      defaultItem  = { selectedProvince }
                      onSelectItem = { onSelectProvince }
                      isError   = { dropdownErrors.province !== '' }
                      errorText = { dropdownErrors.province        }
                    />
                  </CustomView.Column>
                  <CustomView.Column medium={6} className="form-colum dropdown">
                    <LocationDropdowns.City
                      defaultItem      = { selectedCity     }
                      onSelectItem     = { onSelectCity     }
                      selectedProvince = { selectedProvince }
                      isError   = { dropdownErrors.city !== '' }
                      errorText = { dropdownErrors.city        }
                    />
                  </CustomView.Column>
                </CustomView.Row>
                <CustomView.Row className="form-row">
                  <CustomView.Column medium={6} className="form-colum dropdown">
                    <LocationDropdowns.Area
                      defaultItem      = { selectedArea     }
                      onSelectItem     = { onSelectArea     }
                      selectedCity     = { selectedCity     }
                      selectedProvince = { selectedProvince }
                    />
                  </CustomView.Column>
                  <CustomView.Column medium={6} className="form-colum dropdown">
                    <LocationDropdowns.Neighborhood
                      defaultItem      = { selectedNeighborhood }
                      onSelectItem     = { onSelectNieghborhood }
                      selectedCity     = { selectedCity         }
                      selectedArea     = { selectedArea         }
                      selectedProvince = { selectedProvince     }
                    />
                  </CustomView.Column>
                </CustomView.Row>
              </>
            )}
            <CustomView.Row className="form-row">
              <CustomView.Column medium={6} className="form-colum">
                <CustomInput
                  name="phoneNumber"
                  id="phoneNumber"
                  label={t("phoneNumber")}
                  value={formik.values.phoneNumber}
                  onValueChange={formik.handleChange}
                  formikProps={{ onBlur: formik.handleBlur }}
                  error={formik.errors.phoneNumber && formik.touched.phoneNumber}
                  errorText={formik.errors.phoneNumber}
                  maxLength={11}
                />
              </CustomView.Column>
              <CustomView.Column medium={6} className="form-colum dropdown">
                <label className="form-label">{t("gender")}</label>
                <CustomDropdownSearch
                  items={values.dropdownItems.GENDER}
                  itemName="name"
                  placeHolder={t("selectGender")}
                  defaultItem={selectedGender}
                  onSelectItem={(event: any) => {
                    setSelectedGender(event);
                    setDropdownErrors({ ...dropdownErrors, gender: "" });
                  }}
                />
                <div className="error">{dropdownErrors.gender !== "" && dropdownErrors.gender}</div>
              </CustomView.Column>
            </CustomView.Row>
            <CustomView.Row className="form-row">
              <CustomView.Column large={3} medium={4} small={6} className="form-colum">
                <CustomButton2
                  isLoading={isLoading}
                  onClick={() => setShowModal!("NONE")}
                  title={t("cancel")}
                  type="danger"
                />
              </CustomView.Column>
              <CustomView.Column large={3} medium={4} small={6} className="form-colum">
                <CustomButton2 isLoading={isLoading} onClick={submitHandler} title={t("submit")}/>
              </CustomView.Column>
            </CustomView.Row>
          </CustomView.Container>
        </CustomModal>
      </form>
    </section>
  );
};

export default Create;
